import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import {
    assignAndFillPositionFailure,
    assignAndFillPositionSuccess,
    assignCandidateDocumentPages,
    assignCandidateDocumentTypeFailure,
    assignCandidateDocumentTypeSuccess,
    assignCandidateDocumentTypeWithArgs
} from 'src/app/compliance/store/actions/document-upload-tool.actions';
import {
    selectComplianceDocumentReviewerAssignedDocumentState,
    selectComplianceDocumentReviewerDetails
} from 'src/app/compliance/store/selectors/compliance.selectors';
import { showToasterMessage } from '../../../shared/actions/toaster.actions';
import { DocumentUploadToolClient } from '../../api/document-upload-tool-client.service';
import { MetadataValueDto } from '../../api/models/metadata-value-dto.model';
import { CandidateDocumentHistory } from '../../api/models/candidate-document-history.model';
import { AssignDocumentResult } from 'src/app/compliance/models/posted-documents-models';

@Injectable()
export class DocumentUploadEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _documentUploadService: DocumentUploadToolClient,
        private readonly _store: Store
    ) {}

    assignCandidateDocumentType$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(assignCandidateDocumentTypeWithArgs),
            concatLatestFrom(() => this._store.select(selectComplianceDocumentReviewerAssignedDocumentState)),
            exhaustMap(([action, assign]) => {
                return this._documentUploadService
                    .assignDocumentToContract(assign.candidateDocumentId, {
                        pages: assign.pages,
                        contractId: assign.contractId,
                        documentTypeId: assign.documentTypeId,
                        expirationDate: action.expiration.toDate(),
                        resolution: assign.resolution === 0 ? null : assign.resolution,
                        documentAttributes: action.documentAttributes as unknown as MetadataValueDto[],
                        note: action.docNote,
                        candidateDocumentHistory: assign?.candidateDocumentHistory as CandidateDocumentHistory,
                        candidateVaccine: null
                    })
                    .pipe(
                        map((result) => assignCandidateDocumentTypeSuccess({ assignResult: result })),
                        catchError((err: any) => {
                            if (err.status === 409 || err.status === 400) {
                                return of(
                                    assignCandidateDocumentTypeSuccess({
                                        assignResult:
                                            typeof err.response === 'string'
                                                ? (JSON.parse(err.response) as AssignDocumentResult)
                                                : (err.response as AssignDocumentResult)
                                    })
                                );
                            }
                            return of(assignCandidateDocumentTypeFailure({ error: err }));
                        })
                    );
            })
        );
    });

    assignCandidateDocumentTypeError$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(assignCandidateDocumentTypeFailure),
            map(() => showToasterMessage({ message: 'Failed to confirm candidate document!' }))
        );
    });

    assignAndFillPosition$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(assignCandidateDocumentTypeSuccess),
            concatLatestFrom(() => this._store.select(selectComplianceDocumentReviewerAssignedDocumentState)),
            switchMap(([action, docState]) => {
                if (action.assignResult.isSignedContract && docState.contractId > 0 && action.assignResult.success) {
                    return this._documentUploadService
                        .assignAndFillSignedContract({
                            contractId: docState.contractId,
                            documentTypeId: docState.documentTypeId
                        })
                        .pipe(
                            map(() => assignAndFillPositionSuccess()),
                            catchError((error) => of(assignAndFillPositionFailure({ error })))
                        );
                }
                return of(assignAndFillPositionSuccess());
            })
        );
    });

    setAssignedPages$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(assignCandidateDocumentTypeSuccess),
            concatLatestFrom(() => [
                this._store.select(selectComplianceDocumentReviewerAssignedDocumentState),
                this._store.select(selectComplianceDocumentReviewerDetails)
            ]),
            map(([action, assigned, details]) => {
                if (action.assignResult.success) {
                    return assignCandidateDocumentPages({ pageCount: assigned.pages.length });
                }

                return assignCandidateDocumentPages({ pageCount: details.assignedPageCount });
            })
        );
    });
}
